import React, { useState, useEffect } from 'react';
import { Box, Text, VStack, Image, HStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { USER_TYPES } from '../constants';
import { sendLog, sendError, convertTimestampToTimeString, getCachedUserProfile, getCachedInfluencerEvents, getFavorites, removeFromFavorites } from '../utils';
import FavoritesButton from './FavoritesButton';


const UpcomingEventsList: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useUser();
  const [eventsData, setEventsData] = useState<any[]>([]);
  const [favoritesRefresh, setFavoritesRefresh] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch events data
  useEffect(() => {
    const fetchEvents = async () => {
      if (!config?.apiUrl || !user?.id || user.userType !== USER_TYPES.INFLUENCER) {
        return;
      }

      setIsLoading(true);
      try {
        // Fetch influencer profile to get city and country
        const profileData = await getCachedUserProfile(config.apiUrl, user.id, user.userType);
        const influencerCity = profileData.city;
        const influencerCountry = profileData.country;

        if (influencerCity && influencerCountry) {
          const allEvents = await getCachedInfluencerEvents(
            config.apiUrl,
            user.id,
            influencerCity,
            influencerCountry
          );
          setEventsData(allEvents);
        }
        
        sendLog('FavoritesListLoadSuccess', {
          influencerId: user.id
        });
      } catch (error: any) {
        const errorToLog = error instanceof Error 
          ? error 
          : new Error(error.message || 'Failed to fetch events');
        
        sendError(errorToLog, {
          context: 'UpcomingEventsList.fetchEvents',
          influencerId: user.id,
          status: error.response?.status
        });
        
        setEventsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [config?.apiUrl, user?.id, user?.userType]);

  // Transform favorites to events list format
  const events = React.useMemo(() => {
    if (!user?.id || eventsData.length === 0) {
      return [];
    }

    const favoriteEventIds = getFavorites(user.id);
    
    return favoriteEventIds
      .map((eventId) => {
        const event = eventsData.find((e: any) => e.id === eventId);
        
        if (!event) {
          return null;
        }

        // Extract date from startTime timestamp
        let parsedDate: Date | null = null;
        let dateString = '';
        
        if (event.startTime) {
          parsedDate = new Date(event.startTime);
          if (!isNaN(parsedDate.getTime())) {
            dateString = parsedDate.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            });
          }
        }

        // Read timestamp from top-level field and convert to time string
        const timeString = convertTimestampToTimeString(event.startTime);
        
        // Get event image from photos array (first photo if available)
        const eventImage = event.photos && event.photos.length > 0 
          ? event.photos[0] 
          : event.data?.photos?.[0] || '';
        
        return {
          id: event.id,
          name: event.eventName,
          date: dateString,
          time: timeString,
          location: event.data?.eventLocation || event.eventLocation || '',
          day: parsedDate && !isNaN(parsedDate.getTime()) 
            ? parsedDate.toLocaleDateString('en-US', { weekday: 'short' }) 
            : '',
          image: eventImage
        };
      })
      .filter(Boolean); // Remove null entries
  }, [eventsData, user?.id, favoritesRefresh]);

  const handleRemoveFavorite = (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user?.id) {
      return;
    }

    removeFromFavorites(user.id, eventId);
    
    // Trigger re-render to update the list
    setFavoritesRefresh(prev => prev + 1);
    
    sendLog('FavoriteRemoved', {
      eventId,
      influencerId: user.id
    });
  };

  // Check if loading or events list is empty
  if (isLoading) {
    return (
      <Box paddingTop="20px" textAlign="center">
        <Text className="static-text">Loading favorites...</Text>
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box paddingTop="20px" >
        <Text
          className="subtitle-text-center"
          whiteSpace="pre-line"
        >
          0 favorite events
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing="14px" align="stretch" width="100%">
        {events.map((event: any) => (
          <Box
            className="vstack-main-box"
            key={event.id}
            height="275px"
            onClick={() => navigate(`/event/${event.id}`)}
          >
            {/* Event Image */}
            <Box
              className="event-image-box"
              right="0"
            >
              <Image
                className="event-image-small"
                src={event.image}
                alt={event.name}
              />
            </Box>

            {/* Favorite Button */}
            <FavoritesButton
              isFavorite={true}
              onClick={(e) => handleRemoveFavorite(event.id, e)}
            />

            {/* Date Badge */}
            <Box className="event-badge-box" >
              <Text className="item-badge-text" >
                {event.date ? (event.date.includes(' ') ? event.date.split(' ')[0] : event.date) : ''}
              </Text>
              <Text
                className="subtitle-text"
                color="#1D1D1D"
              >
                {event.day || ''}
              </Text>
            </Box>

            {/* Event Details */}
            <Box className="event-big-details-box" >
              <Text
                className="item-event-name-text"
                marginBottom="8px"
              >
                {event.name}
              </Text>

              <HStack spacing="12px" alignItems="center" marginBottom="4px">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M6.5 0C2.91 0 0 2.91 0 6.5C0 10.09 2.91 13 6.5 13C10.09 13 13 10.09 13 6.5C13 2.91 10.09 0 6.5 0ZM6.5 9.75C4.29 9.75 2.5 7.96 2.5 5.75C2.5 3.54 4.29 1.75 6.5 1.75C8.71 1.75 10.5 3.54 10.5 5.75C10.5 7.96 8.71 9.75 6.5 9.75Z"
                    fill="#FFFFFF"
                  />
                  <path
                    d="M6.5 3.25C5.67 3.25 5 3.92 5 4.75C5 5.58 5.67 6.25 6.5 6.25C7.33 6.25 8 5.58 8 4.75C8 3.92 7.33 3.25 6.5 3.25Z"
                    fill="#FFFFFF"
                  />
                </svg>
                <Text className="static-text" >
                  {event.time}
                </Text>
              </HStack>

              <HStack spacing="12px" alignItems="center">
                <svg width="10" height="13" viewBox="0 0 10 13" fill="none">
                  <path
                    d="M5 0C2.24 0 0 2.24 0 5C0 8.5 5 13 5 13C5 13 10 8.5 10 5C10 2.24 7.76 0 5 0ZM5 6.5C4.17 6.5 3.5 5.83 3.5 5C3.5 4.17 4.17 3.5 5 3.5C5.83 3.5 6.5 4.17 6.5 5C6.5 5.83 5.83 6.5 5 6.5Z"
                    fill="#FFFFFF"
                  />
                </svg>
                <Text className="static-text" >
                  {event.location}
                </Text>
              </HStack>
            </Box>
          </Box>
        ))}
    </VStack>
  );
};

export default UpcomingEventsList;
